import { X, Navigation, MapPin, Car, CheckCircle, Timer } from 'lucide-react';
import { Button } from '../ui/button';

export const RideWorkflowModal = ({
  rideModal, setRideModal,
  rideLoading, advanceRideStep,
}) => {
  if (!rideModal) return null;

  const stepIdx = ['en_route','arrived','started','completed'].indexOf(rideModal.ride?.step);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setRideModal(null)}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-white">Course : {rideModal.first_name} {rideModal.last_name}</h3>
          <button onClick={() => setRideModal(null)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-zinc-500 text-xs mb-4">{rideModal.pickup_address} → {rideModal.destination_address}</p>

        {/* Current step indicator */}
        <div className="flex items-center gap-2 mb-6">
          {['en_route', 'arrived', 'started', 'completed'].map((s, i) => (
            <div key={s} className="flex items-center gap-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                (rideModal.ride?.step === s) ? 'bg-emerald-500 text-black' :
                stepIdx > i ? 'bg-emerald-500/30 text-emerald-400' :
                'bg-zinc-800 text-zinc-500'
              }`}>{i + 1}</div>
              {i < 3 && <div className={`w-6 h-0.5 ${stepIdx > i ? 'bg-emerald-500/50' : 'bg-zinc-800'}`} />}
            </div>
          ))}
        </div>

        {/* Step buttons */}
        <div className="space-y-3">
          {(!rideModal.ride?.step || rideModal.ride?.step === 'pending') && (
            <Button onClick={() => advanceRideStep('en_route')} disabled={rideLoading} className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white" data-testid="step-en-route">
              <Navigation className="w-5 h-5 mr-2" /> 1. En route vers le client
            </Button>
          )}
          {rideModal.ride?.step === 'en_route' && (
            <Button onClick={() => advanceRideStep('arrived')} disabled={rideLoading} className="w-full py-4 bg-amber-600 hover:bg-amber-700 text-white" data-testid="step-arrived">
              <MapPin className="w-5 h-5 mr-2" /> 2. Arrive sur place
            </Button>
          )}
          {rideModal.ride?.step === 'arrived' && (
            <>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
                <Timer className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                <p className="text-amber-400 text-sm font-bold">Temps d'attente en cours...</p>
                <p className="text-zinc-500 text-xs">Depuis {rideModal.ride?.arrived_at ? new Date(rideModal.ride.arrived_at).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'}) : '-'}</p>
              </div>
              <Button onClick={() => advanceRideStep('started')} disabled={rideLoading} className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="step-started">
                <Car className="w-5 h-5 mr-2" /> 3. Demarrer la course
              </Button>
            </>
          )}
          {rideModal.ride?.step === 'started' && (
            <>
              {(rideModal.stops || []).length > 0 && (
                <div className="bg-zinc-800 rounded-lg p-3">
                  <p className="text-zinc-400 text-xs mb-2">Arrets intermediaires</p>
                  {(rideModal.ride?.stop_waits || []).some(sw => !sw.end) ? (
                    <Button onClick={() => advanceRideStep('stop_wait_end')} className="w-full bg-orange-600 hover:bg-orange-700 text-white text-sm" data-testid="stop-wait-end">
                      <Timer className="w-4 h-4 mr-2" /> Fin d'attente arret
                    </Button>
                  ) : (
                    <Button onClick={() => advanceRideStep('stop_wait_start')} variant="outline" className="w-full border-orange-500/50 text-orange-400 text-sm" data-testid="stop-wait-start">
                      <Timer className="w-4 h-4 mr-2" /> Demarrer attente arret
                    </Button>
                  )}
                </div>
              )}
              <Button onClick={() => advanceRideStep('completed')} disabled={rideLoading} className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="step-completed">
                <CheckCircle className="w-5 h-5 mr-2" /> 4. Terminer la course
              </Button>
            </>
          )}
          {rideModal.ride?.step === 'completed' && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 text-center">
              <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-emerald-400 font-bold">Course terminee</p>
              {rideModal.ride?.pickup_wait_minutes > 0 && <p className="text-zinc-400 text-xs">Attente arrivee: {rideModal.ride.pickup_wait_minutes} min</p>}
              {rideModal.ride?.ride_duration_minutes > 0 && <p className="text-zinc-400 text-xs">Duree course: {Math.round(rideModal.ride.ride_duration_minutes)} min</p>}
              {rideModal.ride?.total_wait_minutes > 0 && <p className="text-zinc-400 text-xs">Attente totale: {rideModal.ride.total_wait_minutes} min</p>}
            </div>
          )}
        </div>

        {/* Ride info summary */}
        {rideModal.ride?.step && rideModal.ride.step !== 'pending' && (
          <div className="mt-4 pt-4 border-t border-zinc-800 space-y-1">
            {rideModal.ride.en_route_at && <p className="text-zinc-500 text-xs">En route: {new Date(rideModal.ride.en_route_at).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})}</p>}
            {rideModal.ride.arrived_at && <p className="text-zinc-500 text-xs">Arrive: {new Date(rideModal.ride.arrived_at).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})}</p>}
            {rideModal.ride.started_at && <p className="text-zinc-500 text-xs">Depart: {new Date(rideModal.ride.started_at).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})}</p>}
            {rideModal.ride.completed_at && <p className="text-zinc-500 text-xs">Fin: {new Date(rideModal.ride.completed_at).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})}</p>}
          </div>
        )}
      </div>
    </div>
  );
};
